import { Component, inject, signal } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { InvoicingService } from '../../core/services/invoicing.service';
import { MerchantsService } from '../../core/services/merchants.service';
import { ToolsService } from '../../core/services/tools.service';
import { toSignal } from '@angular/core/rxjs-interop';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { TranslatePipe } from '@ngx-translate/core';
import { TabBarComponent } from '../../shared/tab-bar/tab-bar.component';
import { TextInputComponent } from '../../shared-with-client/text-input/text-input.component';
import { MerchantCurrencyPipe } from '../../shared/pipes/merchant-currency.pipe';

@Component({
  selector: 'app-invoice-discount-modal',
  imports: [TranslatePipe, TabBarComponent, TextInputComponent, ReactiveFormsModule, MerchantCurrencyPipe],
  templateUrl: './invoice-discount-modal.component.html',
  styleUrl: './invoice-discount-modal.component.scss'
})
export class InvoiceDiscountModalComponent {
    private invoicingService = inject(InvoicingService);
    private merchantService = inject(MerchantsService);
    private toolsService = inject(ToolsService);
    public currentMerchant = toSignal(this.merchantService.activeMerchant);

    public loadingTotals = signal(false);
    public totalsSimulation = signal<any>(this.invoicingService.invoicingCartTotals$.value || {});
    public totalInvoicingCartTotals = toSignal(this.invoicingService.invoicingCartTotals$, { initialValue: {} });

    public bsModalRef = inject(BsModalRef);

    submitAttempted = true;
    saving = signal(false);

    formBuilder = inject(FormBuilder);
    form!: FormGroup;

    activeTab = 'discountAmountCents';
    tabOptions = [
        {
        label: (this.toolsService.formatAsCurrency(0, this.currentMerchant()?.currency)?.replace('0', '') || '€')?.trim(),
        value: 'discountAmountCents',
        },
        { label: '%', value: 'discountPercentage' },
    ];

    ngOnInit(): void {
        this.generateForm();
        this.activeTab = this.invoicingService.invoicingCart$.value.discountPercentage && this.invoicingService.invoicingCart$.value.discountPercentage > 0 ? 'discountPercentage' : 'discountAmountCents';
    }

    generateForm() {
        const maxDiscount = (this.totalInvoicingCartTotals()?.totalCents || 0) / 100;
        this.form = this.formBuilder.group({
            discountPercentage: [this.invoicingService.invoicingCart$.value.discountPercentage || 0, [Validators.min(0), Validators.max(100)]],
            discountAmountCents: [
                (this.invoicingService.invoicingCart$.value.discountAmountCents || 0) / 100,
                [Validators.min(0), Validators.max(maxDiscount)],
            ],
        });

        this.form.valueChanges.subscribe(() => {
            this.loadingTotals.set(true);
            const discountPercentage = this.activeTab === 'discountPercentage' ? this.form.value.discountPercentage || 0 : 0;
            const discountAmountCents = this.activeTab === 'discountAmountCents' ? Math.round((this.form.value.discountAmountCents || 0) * 100) : 0;
            const currentCart = this.invoicingService.invoicingCart$.value;
            currentCart.discountPercentage = discountPercentage;
            currentCart.discountAmountCents = discountAmountCents;
            if (this.form.invalid) {
                this.loadingTotals.set(false);
                return;
            }
            this.invoicingService.calculateCartTotal(currentCart).subscribe((res) => {
                this.totalsSimulation.set(res);
                this.loadingTotals.set(false);
            });
        });
    }

    onTabChanged(tab: string) {
        if (tab === 'discountAmountCents') {
            this.form.get('discountAmountCents')?.enable();
            this.form.get('discountPercentage')?.disable();
        } else if (tab === 'discountPercentage') {
            this.form.get('discountPercentage')?.enable();
            this.form.get('discountAmountCents')?.disable();
        }
    }

    save() {
        this.submitAttempted = true;
        if (this.form.invalid) {
            return;
        }
        this.saving.set(true);
        const formValue = this.form.value;
        this.invoicingService.invoicingCart$.next({
            ...this.invoicingService.invoicingCart$.value,
            discountPercentage: this.activeTab === 'discountPercentage' ? formValue.discountPercentage || 0 : 0,
            discountAmountCents: this.activeTab === 'discountAmountCents' ? Math.round((formValue.discountAmountCents * 100) || 0) : 0,
        });
        this.saving.set(false);
        this.close();
    }

    close() {
      this.bsModalRef.hide();
    }
}
